/**
 * Add or edit a registered template, and say where each variable's value comes from.
 *
 * The text is pasted exactly as registered — {#var#} slots and all — because a
 * message that does not match its registration is dropped by the operator. What
 * the admin adds is a source per slot, in order:
 *
 * - **Form** — typed once when sending, the same for every recipient (an occasion);
 * - **Contact** — read from each recipient (their first name), from the fixed list
 *   of contact fields the API publishes.
 *
 * The slots are counted as the text is typed, so the variable rows always line up
 * with them. The server checks all of this again; the checks here only save a trip.
 *
 * The provider's own template fields (a DLT template ID) and its categories come
 * from the catalog, like its credentials do.
 */

import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQueries, useQuery } from '@tanstack/react-query';
import {
  ApiError,
  Alert,
  Button,
  Input,
  Modal,
  Select,
  Switch,
  Textarea,
  api,
  toast,
} from '@connect/web-shared';

import { ProviderFields, fieldsValid, initialValues, requiredFilled } from './provider-fields';
import { billingClass, designFrom, designPayload, designProblems, type RcsDesign } from './rcs-design';
import { RcsDesignEditor, type FallbackOption } from './rcs-design-editor';
import { TemplateHeaderEditor } from './template-header-editor';
import {
  fetchClientChannels,
  fetchVariableCatalog,
  messagingKeys,
  type ChannelDef,
  type ChannelTemplate,
  type ClientChannel,
  type ProviderDef,
  type TemplateVariable,
  type VariableSource,
} from './types';

const SLOT = '{#var#}';
const KEY = /^[a-z][a-z0-9_]{0,39}$/;

function slotsIn(body: string): number {
  return body.split(SLOT).length - 1;
}

/** The label the server gives a form variable sent without one. */
function labelFor(key: string): string {
  const words = key.replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

export function ChannelTemplateDialog({
  channel,
  def,
  provider,
  clientName,
  template,
  open,
  onClose,
  onSaved,
}: {
  channel: ClientChannel;
  def: ChannelDef;
  provider: ProviderDef;
  clientName: string;
  /** `null` adds a new one. */
  template: ChannelTemplate | null;
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState('');
  const [providerId, setProviderId] = useState('');
  const [category, setCategory] = useState('');
  const [encoding, setEncoding] = useState('');
  const [attributes, setAttributes] = useState<Record<string, string>>({});
  const [body, setBody] = useState('');
  const [active, setActive] = useState(true);
  const [variables, setVariables] = useState<(TemplateVariable | null)[]>([]);
  const [design, setDesign] = useState<RcsDesign>(designFrom(null));
  // The header is saved as it is chosen, so the dialog shows the template as
  // the server now has it rather than as it opened.
  const [current, setCurrent] = useState<ChannelTemplate | null>(template);

  const catalog = useQuery({
    queryKey: messagingKeys.variableCatalog,
    queryFn: fetchVariableCatalog,
    enabled: open,
    staleTime: Infinity,
  });

  useEffect(() => {
    if (!open) return;
    setName(template?.name ?? '');
    setProviderId(template?.provider_template_id ?? '');
    setCategory(template?.category ?? provider.template_categories[0] ?? '');
    setEncoding(template?.encoding ?? provider.template_encodings[0]?.value ?? '');
    setAttributes(initialValues(provider.template_attributes, template?.attributes));
    setBody(template?.body ?? '');
    setActive(template?.is_active ?? true);
    setVariables(template?.variables ?? []);
    setDesign(designFrom(template?.rich));
    setCurrent(template);
  }, [open, template, provider]);

  // A provider that syncs its templates is where their IDs come from — its own
  // template ID and the DLT registration arrive with the sync and are shown in the
  // templates list. Editing one here only invites a typo that breaks every send,
  // so they are neither shown nor sent. Adding a template by hand, on a provider
  // that cannot sync, is the one place they are typed.
  const idsFromProvider = template !== null && channel.capabilities.includes('sync_templates');
  // The provider holds the definition — text, category — and only a sync changes
  // it (WhatsApp). What is set up here is where each slot's value comes from.
  const definedByProvider = !provider.manual_templates;
  // RCS: Google keeps no templates, so the code is Connect's own and the
  // design round the text is set up here.
  const rich = provider.rich_templates;
  // The client's SMS templates, for an RCS message's fallback.
  const services = useQuery({
    queryKey: messagingKeys.channels(channel.client_public_id),
    queryFn: () => fetchClientChannels(channel.client_public_id),
    enabled: open && rich,
  });
  const smsServices = (services.data ?? []).filter((c) => c.channel === 'sms');
  const smsTemplates = useQueries({
    queries: smsServices.map((sms) => ({
      queryKey: ['messaging', 'templates', sms.public_id],
      queryFn: () => api.get<ChannelTemplate[]>(`/messaging/channels/${sms.public_id}/templates`),
      enabled: open && rich,
    })),
  });
  const fallbacks: FallbackOption[] = smsTemplates
    .flatMap((q) => q.data ?? [])
    .filter((t) => t.is_active)
    .map((t) => ({ public_id: t.public_id, name: t.name, variable_count: t.variable_count }));

  const count = useMemo(() => slotsIn(body), [body]);
  // One row per slot: the ones that still line up keep their variable.
  const aligned = useMemo(
    () => Array.from({ length: count }, (_, index) => variables[index] ?? null),
    [count, variables],
  );

  const setSlot = (index: number, variable: TemplateVariable | null) => {
    const next = [...aligned];
    next[index] = variable;
    setVariables(next);
  };

  const problems = useMemo(() => {
    const out: string[] = [];
    const labels = new Map<string, string>();
    aligned.forEach((variable, index) => {
      if (!variable) return;
      if (variable.source === 'contact') {
        if (!variable.key) out.push(`Choose a contact field for variable ${index + 1}.`);
        return;
      }
      if (!KEY.test(variable.key)) {
        out.push(
          `Variable ${index + 1} needs a key: lowercase letters, digits and underscores, starting with a letter.`,
        );
        return;
      }
      const label = variable.label.trim() || labelFor(variable.key);
      const seen = labels.get(variable.key);
      if (seen !== undefined && seen !== label)
        out.push(`The form field ${variable.key} has two labels; it is asked for once, so give it one.`);
      labels.set(variable.key, label);
    });
    return out;
  }, [aligned]);

  // The text as a sender will read it, with each slot showing what fills it.
  const preview = useMemo(() => {
    const parts = body.split(SLOT);
    return parts
      .map((part, index) => {
        if (index === 0) return part;
        const variable = aligned[index - 1];
        const shown = !variable?.key
          ? `variable ${index}`
          : variable.source === 'form'
            ? variable.label.trim() || labelFor(variable.key)
            : `contact's ${variable.label.toLowerCase()}`;
        return `[${shown}]${part}`;
      })
      .join('');
  }, [body, aligned]);

  const designIssues = useMemo(() => (rich ? designProblems(design, body) : []), [rich, design, body]);

  const save = useMutation({
    mutationFn: () => {
      const payload = {
        name,
        ...(idsFromProvider
          ? {}
          : {
              provider_template_id: providerId,
              // Only the fields with a value: an empty optional field is not set.
              attributes: Object.fromEntries(
                Object.entries(attributes).filter(([, value]) => value),
              ),
            }),
        ...(provider.template_categories.length && !definedByProvider ? { category } : {}),
        ...(provider.template_encodings.length ? { encoding } : {}),
        ...(definedByProvider ? {} : { body }),
        ...(rich ? { rich: designPayload(design) } : {}),
        variables: aligned.map((variable) =>
          variable === null
            ? null
            : {
                ...(variable.source === 'contact'
                  ? { source: 'contact', key: variable.key }
                  : { source: 'form', key: variable.key, label: variable.label.trim() || null }),
                // Only when set: every template saved before stays as it was.
                ...(variable.sensitive ? { sensitive: true } : {}),
              },
        ),
        is_active: active,
      };
      return template
        ? api.patch<ChannelTemplate>(`/messaging/templates/${template.public_id}`, payload)
        : api.post<ChannelTemplate>(`/messaging/channels/${channel.public_id}/templates`, payload);
    },
    onSuccess: (saved) => {
      onSaved();
      toast.success(
        template ? 'Template saved' : 'Template added',
        saved.is_ready ? 'Ready to send with.' : 'Set up every variable to make it ready.',
      );
      onClose();
    },
  });

  const fieldErrors = save.error instanceof ApiError ? save.error.fieldErrors : undefined;
  const ready =
    Boolean(name.trim() && providerId.trim() && body.trim()) &&
    problems.length === 0 &&
    designIssues.length === 0 &&
    requiredFilled(provider.template_attributes, attributes) &&
    fieldsValid(provider.template_attributes, attributes);

  const sourceOptions = (catalog.data?.sources ?? []).map((source) => ({
    value: source.id,
    label: source.label,
  }));
  const contactOptions = (catalog.data?.contact_fields ?? []).map((field) => ({
    value: field.key,
    label: field.label,
  }));

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={template ? 'Edit template' : 'Add template'}
      description={`A ${def.template_label} on ${clientName}'s ${provider.label} account.`}
      size="lg"
      dismissOnBackdrop={false}
      footer={
        <>
          <Button variant="secondary" size="sm" onClick={onClose} disabled={save.isPending}>
            Cancel
          </Button>
          <Button size="sm" loading={save.isPending} disabled={!ready} onClick={() => save.mutate()}>
            {template ? 'Save' : 'Add template'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {save.error ? (
          <Alert tone="danger" title="Could not save">
            {save.error instanceof ApiError ? save.error.message : 'Could not reach the server.'}
          </Alert>
        ) : null}

        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="Name"
            required
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="Order shipped"
            containerClassName="sm:col-span-2"
          />
          {idsFromProvider ? null : (
            <Input
              label={rich ? 'Message code' : `${provider.label} template ID`}
              required
              value={providerId}
              maxLength={64}
              spellCheck={false}
              onChange={(event) => setProviderId(event.target.value.trim())}
              hint={
                rich
                  ? "Your own short name for it, unique on this service — the provider keeps no templates."
                  : "The provider's own ID for the template — what a send names it by."
              }
            />
          )}
          {provider.template_categories.length && !definedByProvider ? (
            <Select
              label="Category"
              options={provider.template_categories.map((value) => ({ value, label: value }))}
              value={category}
              onChange={(event) => setCategory(event.target.value)}
            />
          ) : null}
          {provider.template_encodings.length ? (
            <Select
              label="SMS encoding"
              options={provider.template_encodings}
              value={encoding}
              onChange={(event) => setEncoding(event.target.value)}
              error={fieldErrors?.['encoding']}
              hint="How every message sent with this template is encoded. Unicode for text outside the English alphabet."
            />
          ) : null}
        </div>

        {idsFromProvider ? null : (
          <ProviderFields
            specs={provider.template_attributes}
            values={attributes}
            onChange={setAttributes}
            errors={fieldErrors}
            errorPrefix="attributes"
          />
        )}

        {current?.header_needed ? (
          <TemplateHeaderEditor
            template={current}
            onChanged={(saved) => {
              setCurrent(saved);
              onSaved();
            }}
          />
        ) : null}

        <Switch
          label="Available to send with"
          checked={active}
          onChange={(event) => setActive(event.target.checked)}
        />

        {definedByProvider ? (
          <div className="space-y-1">
            <p className="text-xs font-medium text-muted">
              Template text{template?.category ? ` · ${template.category}` : ''}
            </p>
            <p className="whitespace-pre-wrap rounded-control bg-surface-2 px-2.5 py-1.5 text-xs text-body">
              {body}
            </p>
            <p className="text-2xs text-faint">
              As {provider.label} approved it; edit it there and sync. Each {'{#var#}'} is one of its
              variables.
            </p>
          </div>
        ) : (
          <Textarea
            label={
              !rich
                ? 'Template text'
                : design.layout === 'text'
                  ? 'Message text'
                  : design.layout === 'card'
                    ? "The card's text"
                    : "The first card's text"
            }
            required
            rows={4}
            value={body}
            onChange={(event) => setBody(event.target.value)}
            hint={
              rich
                ? `Put {#var#} where each recipient's details go. ${count} slot${count === 1 ? '' : 's'}, ${body.length} characters — billed as a ${billingClass(design, body)} message.`
                : `Paste it exactly as registered. ${count} variable slot${count === 1 ? '' : 's'} ({#var#}), ${body.length} characters.`
            }
            spellCheck={false}
          />
        )}

        {rich ? (
          <div className="space-y-2">
            <RcsDesignEditor design={design} onChange={setDesign} slots={count} fallbacks={fallbacks} />
            {designIssues.map((issue) => (
              <p key={issue} className="text-xs text-danger" role="alert">
                {issue}
              </p>
            ))}
          </div>
        ) : null}

        {count > 0 ? (
          <div className="space-y-3">
            <div className="space-y-0.5">
              <p className="text-xs font-medium text-muted">Variables, in order</p>
              <p className="text-2xs text-faint">
                {(catalog.data?.sources ?? [])
                  .map((source) => `${source.label}: ${source.description}`)
                  .join(' ')}
              </p>
            </div>
            {catalog.isError ? (
              <Alert tone="danger" title="Could not load the variable sources">
                Close this and try again.
              </Alert>
            ) : null}
            {aligned.map((variable, index) => (
              <div key={index} className="grid gap-3 sm:grid-cols-3">
                <Select
                  label={`Variable ${index + 1}`}
                  placeholder="Not set up"
                  options={sourceOptions}
                  value={variable?.source ?? ''}
                  onChange={(event) => {
                    const source = event.target.value as VariableSource | '';
                    setSlot(index, source ? { source, key: '', label: '' } : null);
                  }}
                />
                {variable?.source === 'contact' ? (
                  <Select
                    label="Contact field"
                    placeholder="Choose a field"
                    options={contactOptions}
                    value={variable.key}
                    containerClassName="sm:col-span-2"
                    onChange={(event) => {
                      const field = catalog.data?.contact_fields.find(
                        (f) => f.key === event.target.value,
                      );
                      setSlot(index, { source: 'contact', key: field?.key ?? '', label: field?.label ?? '' });
                    }}
                  />
                ) : null}
                {variable?.source === 'form' ? (
                  <>
                    <Input
                      label="Key"
                      value={variable.key}
                      placeholder="occasion"
                      spellCheck={false}
                      autoCapitalize="none"
                      onChange={(event) =>
                        setSlot(index, { ...variable, key: event.target.value.trim().toLowerCase() })
                      }
                    />
                    <Input
                      label="Label on the send form"
                      value={variable.label}
                      maxLength={60}
                      placeholder={variable.key ? labelFor(variable.key) : 'Occasion'}
                      onChange={(event) => setSlot(index, { ...variable, label: event.target.value })}
                    />
                  </>
                ) : null}
                {variable ? (
                  <div className="sm:col-span-3">
                    <Switch
                      label="Private value"
                      checked={Boolean(variable.sensitive)}
                      hint="A one-time or reset code: sent, then shown as •••••• in history, previews, exports and webhooks, and never kept."
                      onChange={(event) => setSlot(index, { ...variable, sensitive: event.target.checked })}
                    />
                  </div>
                ) : null}
              </div>
            ))}
            {problems.map((problem) => (
              <p key={problem} className="text-xs text-danger" role="alert">
                {problem}
              </p>
            ))}
            <div className="space-y-1">
              <p className="text-xs font-medium text-muted">Preview</p>
              <p className="whitespace-pre-wrap rounded-control bg-surface-2 px-2.5 py-1.5 text-xs text-body">
                {preview}
              </p>
            </div>
          </div>
        ) : null}
      </div>
    </Modal>
  );
}
